import React, { useContext, useEffect, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { DarkModeContext } from "../../context/darkModeContext";

/**
 * Thin wrapper over echarts-for-react that:
 *  - rebuilds the option when the theme flips (chart chrome is read from CSS
 *    custom properties, which ECharts cannot observe on its own),
 *  - resizes with its container rather than only with the window,
 *  - exposes the underlying instance through `chartRef` for image export.
 *
 * `buildOption` is a function so the option is recomputed *after* the new
 * theme tokens have been applied, not captured at render time.
 */
const Chart = ({ buildOption, height = 320, chartRef, className, deps = [] }) => {
  const { darkMode } = useContext(DarkModeContext);
  const innerRef = useRef(null);
  const containerRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const option = useMemo(() => buildOption(), [darkMode, ...deps]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      innerRef.current?.getEchartsInstance?.().resize();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={className} style={{ width: "100%", height }}>
      <ReactECharts
        ref={(instance) => {
          innerRef.current = instance;
          if (chartRef) chartRef.current = instance;
        }}
        option={option}
        notMerge
        lazyUpdate
        style={{ width: "100%", height: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};

export default Chart;
